import { RecurseObject } from '~common-interfaces/helper.interface';
import { NotificationsModule } from '~common-store/modules/notifications';

export function computePercentage(numerator: number, denominator: number, numberOfDecimals = 0): string {
    return (numerator / denominator * 100).toFixed(numberOfDecimals);
}

export function parseIntOrSetToString(str: string): string | number {
    const parsedNumber = parseInt(str, 10);
    return isNaN(parsedNumber) ? str : parsedNumber;
}

export function removeEmptyValues(obj: RecurseObject | RecurseObject[]): RecurseObject | RecurseObject[] {
    // if the current object is not an array
    if (!Array.isArray(obj)) {
        // declare new object
        const newObj: RecurseObject = {};
        // iterate over its key
        Object.keys(obj).forEach((key) => {
            if (obj[key] && typeof obj[key] === 'object') {
                // if current value is truthy and is an object, recurse
                newObj[key] = removeEmptyValues(obj[key] as RecurseObject);
            } else if (obj[key] !== null && obj[key] !== '' && obj[key] !== undefined) {
                // if current value is truthy and is not an object, copy the value
                // this will therefore remove falsy values (null, empty strings...)
                newObj[key] = obj[key];
            }
            if (typeof newObj[key] === 'object' && Object.keys(newObj[key]).length === 0) {
                // if value is an object and has no keys, delete it
                delete (newObj[key]);
            }
        });
        return newObj;
    } else {
        // declare new array
        const newArray: RecurseObject[] = [];
        obj.forEach((value: RecurseObject) => {
            // recursively remove the array entry's empty values
            const parsedArrayValue = removeEmptyValues(value) as RecurseObject;
            // if the entry is an object and has at least one key, add it to the array
            if (typeof parsedArrayValue === 'object' && Object.keys(parsedArrayValue).length > 0) {
                newArray.push(parsedArrayValue);
            }
        });
        return newArray;
    }
}

export const userMailRe = /^(vaillant:\/\/|sdbg:\/\/|legrand:\/\/|muller:\/\/|velux:\/\/)*(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const userIdDbRe = /^([0-9A-Fa-f]{24}|[a-f0-9]{8}-([a-f0-9]{4}-){3}[a-f0-9]{12})$/;

export const deviceIdDbRe = /^[0-9a-f]{2}(:[0-9a-f]{2}){5}((:[0-9a-f]{2}){2})?$/;

export const gatewaySomfy = /^somfy:\/\/.*/;

export const deviceIdDbNoSemiColumnRe = /^[0-9a-f]{2}([0-9a-f]{2}){5}(([0-9a-f]{2}){2})?$/;

export const serialRe = /^(g|h|i|j|k|l|m|n|o|p|q|mg|t)[0-9a-f]{6}$/;

export const orderIdRe = /(\d{9}(-\d)?)|(\d{10})|[a-z]{2}-[0-9]{8}/;
export const sapIdRe = /^[0-9]{5,7}$/;

export const guidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export function humanReadableValueFromMap(map: Map<string, string>, value: string): string | undefined {
    value = defaultEmptyValue(value) || value;
    return map.get(value) ? map.get(value) : value;
}

export function defaultEmptyValue(value?: any): string | void {
    if (typeof value === 'undefined' || value === '' || value === null) {
        return 'N/A';
    }
}

export function copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value);
    NotificationsModule.pushNotification({
        type: 'success',
        title: 'Element copied',
        text: value,
        duration: 500,
    });
}

export function castToMajorMinorFix(firmware?: number, type?: string) {
    if (!firmware || firmware < 0) {
        return;
    }
    let majorDivider = 1;
    let minorDivider = 1;
    switch (type) {
        case 'NSD':
        case 'NDL':
        case 'ELG':
        case 'NAC':
        case 'NCO':
        case 'NLE':
            majorDivider = 1000;
            minorDivider = 10;
            break;
        case 'NACamera':
        case 'NDB':
        case 'NOC':
        case 'NOC2':
        case 'NPC':
        case 'NIE':
            majorDivider = 1000000;
            minorDivider = 1000;
            break;
        case 'BNCX':
        case 'BNMH':
        case 'BNHY':
            majorDivider = 10000;
            minorDivider = 100;
            break;
        default:
            return;
    }
    const major = Math.floor(firmware / majorDivider);
    const minor = Math.floor((firmware % majorDivider) / minorDivider);
    const fix = firmware % minorDivider;
    return [major, minor, fix].join('.');
}
